import React, { useEffect, useState } from "react";
import { listAlerts } from "../lib/api";
import { Link } from "react-router-dom";
import { ChevronRight, MapPin } from "lucide-react";
import { useT } from "../lib/lang";
import { InterventionPill } from "../components/InterventionStatus";

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
  } catch (_e) { return iso; }
};

const StatusPill = ({ s, t }) => {
  const map = {
    delivered: "bg-emerald-600 text-white",
    partial: "bg-amber-600 text-white",
    in_app_only: "bg-rescue-ink text-white",
    dispatching: "bg-amber-600 text-white",
  };
  const cls = map[s] || "bg-neutral-200 text-rescue-ink";
  return <span className={`px-2 py-1 text-[9px] font-bold uppercase tracking-[0.2em] ${cls}`}>{t.pill[s] || s}</span>;
};

const USER_FACING_LABELS = {
  received:        "Reçue",
  under_review:    "En examen",
  in_process:      "En traitement",
  help_on_the_way: "Secours assignés",
  resolved:        "Résolue",
  closed:          "Clôturée",
};
const UserFacingPill = ({ s }) => {
  if (!s) return null;
  const color = {
    received:        "bg-zinc-100 text-zinc-700 border-zinc-300",
    under_review:    "bg-sky-100 text-sky-800 border-sky-300",
    in_process:      "bg-cyan-100 text-cyan-800 border-cyan-300",
    help_on_the_way: "bg-amber-100 text-amber-800 border-amber-300",
    resolved:        "bg-emerald-100 text-emerald-800 border-emerald-300",
    closed:          "bg-zinc-100 text-zinc-700 border-zinc-300",
  }[s] || "bg-neutral-100 text-neutral-700 border-neutral-300";
  return (
    <span className={`px-2 py-1 text-[9px] font-bold uppercase tracking-[0.2em] border ${color}`}>
      {USER_FACING_LABELS[s] || s}
    </span>
  );
};

export default function HistoryPage() {
  const [t, labels] = useT();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await listAlerts();
        if (!cancelled) setAlerts(data);
      } finally { if (!cancelled) setLoading(false); }
    };
    load();
    const id = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return (
    <div className="flex-1 flex flex-col" data-testid="history-page">
      <header className="px-5 pt-5 pb-3">
        <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-rescue-mute">{t.historyKicker}</div>
        <h1 className="font-display text-3xl font-black tracking-tight leading-none mt-1">{t.historyTitle}</h1>
        <p className="text-sm text-rescue-mute mt-2">{t.historyIntro}</p>
      </header>

      <div className="px-5 pb-8">
        <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-rescue-mute mb-2">
          {loading ? t.loading : t.records(alerts.length)}
        </div>
        <div className="border-2 border-rescue-line bg-white" data-testid="alerts-list">
          {alerts.length === 0 && !loading && (
            <div className="px-4 py-10 text-center text-sm text-rescue-mute">{t.noAlerts}</div>
          )}
          {alerts.map((a) => {
            const sent = a.deliveries.filter((d) => d.status === "sent").length;
            const failed = a.deliveries.filter((d) => d.status === "failed").length;
            const label = labels[a.emergency_type]?.label || a.emergency_type;
            return (
              <Link
                key={a.id}
                to={`/alerts/${a.id}/status`}
                data-testid={`alert-row-${a.id}`}
                className="px-4 py-3 border-b border-rescue-line last:border-b-0 flex items-start justify-between gap-3 hover:bg-rescue-bg transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-lg font-black tracking-tight">{label}</span>
                    <StatusPill s={a.status_summary} t={t} />
                    <UserFacingPill s={a.user_facing_status} />
                    <InterventionPill alert={a} />
                  </div>
                  <div className="font-mono text-[11px] text-rescue-mute mt-1 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" />
                    {a.coordinates.latitude.toFixed(4)}, {a.coordinates.longitude.toFixed(4)}
                  </div>
                  <div className="text-[11px] text-rescue-mute mt-1">{formatDate(a.created_at)}</div>
                  {a.description && <p className="text-xs text-rescue-ink mt-1 line-clamp-2">{a.description}</p>}
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] mt-2 text-rescue-mute">
                    {sent} {t.sentLabel} · {failed} {t.failedLabel} · {t.channels(a.deliveries.length)}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-rescue-mute mt-1" />
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
